import React from 'react';
import { motion } from 'framer-motion';
import { User, KeyRound, Check, Copy } from 'lucide-react';
import { DjayAvatar } from '../../../mascot';
import { ConfettiCelebration } from '../ConfettiCelebration';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterSuccessStepProps {
  role: Role;
  name: string;
  generatedUsername: string;
  generatedLinkCode: string;
  onCopyLinkCode: () => void;
  copied: boolean;
}
export function RegisterSuccessStep({
  role,
  name,
  generatedUsername,
  generatedLinkCode,
  onCopyLinkCode,
  copied
}: RegisterSuccessStepProps) {
  return <motion.div key="step-success" initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.4
  }} className="text-center py-4 relative" data-id="element-457" data-mp="1fn1mxc93a9wr">
      <ConfettiCelebration data-id="element-458" data-mp="1oln2qzn6fatu" />

      <motion.h2 className="text-2xl font-black text-slate-900 mb-2" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-459" data-mp="1jpetg2vnubul">
        Welkom bij Meester djay! 🎉
      </motion.h2>

      {role === 'learner' ? <motion.div className="space-y-4" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45
    }} data-id="element-460" data-mp="i3bwl7qsdnf8">
          <p className="text-slate-600 font-medium" data-id="element-461" data-mp="vzfnar2gbdmy">
            Bewaar deze gegevens goed:
          </p>
          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 text-left space-y-3" data-id="element-462" data-mp="10sihn1f1yr47">
            <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-orange-100" data-id="element-463" data-mp="15q1t5tkd8psg">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-464" data-mp="1antmms0nsjyr">
                <User className="w-5 h-5" data-id="element-465" data-mp="3brw579qrb4mf" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-466" data-mp="3bsdxxwe7n0pl">
                <div className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-0.5" data-id="element-467" data-mp="36tktetgwik1k">
                  Gebruikersnaam
                </div>
                <div className="font-mono font-bold text-lg text-orange-900 truncate" data-id="element-468" data-mp="3kmkav108uobr">
                  {generatedUsername}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-orange-100" data-id="element-469" data-mp="3ugso6hgpeapc">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-470" data-mp="3pjaq5m485fw4">
                <KeyRound className="w-5 h-5" data-id="element-471" data-mp="3cceahdk5yaj" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-472" data-mp="3d3ykw1pg9li">
                <div className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-0.5" data-id="element-473" data-mp="294ra8zzahbxh">
                  Koppelcode
                </div>
                <div className="font-mono font-black text-xl tracking-[0.2em] text-orange-900 truncate" data-id="element-474" data-mp="255s79kx4bp8o">
                  {generatedLinkCode}
                </div>
              </div>
              <button onClick={onCopyLinkCode} className="p-2 rounded-lg hover:bg-orange-50 text-orange-500 transition-colors flex-shrink-0" title="Kopieer code" data-id="element-475" data-mp="1z0ue9sv0stac">
                {copied ? <Check className="w-5 h-5 text-emerald-500" data-id="element-476" data-mp="1bb9u56n4rpzh" /> : <Copy className="w-5 h-5" data-id="element-477" data-mp="1gamenkqj4kyw" />}
              </button>
            </div>
            <p className="text-xs text-orange-600 mt-2 font-medium text-center" data-id="element-478" data-mp="12bcxahql7ktc">
              💡 Deel deze code met je ouder(s) of docent!
            </p>
          </div>
          <p className="text-sm text-slate-400 font-medium animate-pulse" data-id="element-479" data-mp="17cag8wjf8ivw">
            Je wordt zo ingelogd...
          </p>
        </motion.div> : role === 'teacher' ? <motion.div className="space-y-4" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45
    }} data-id="element-480" data-mp="31np7jhs2yqdr">
          <p className="text-slate-600 font-medium" data-id="element-481" data-mp="rmqo8prbebq6">
            Welkom, meester/juf! Je docentaccount is klaar.
          </p>
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4" data-id="element-482" data-mp="nlwzju61ssw8">
            <p className="text-sm text-indigo-800 font-medium" data-id="element-483" data-mp="invgh98mgtkr">
              In het docentportaal kun je leerlingen toevoegen aan je klas.
            </p>
          </div>
          <p className="text-sm text-slate-400 font-medium animate-pulse" data-id="element-484" data-mp="8wr3gf2ibisk">
            Naar je dashboard...
          </p>
        </motion.div> : <motion.div className="space-y-4" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45
    }} data-id="element-485" data-mp="3w4g6yhb8rg6">
          <p className="text-slate-600 font-medium" data-id="element-486" data-mp="26kcs8x2tga45">
            Welkom! Je ouderaccount is aangemaakt.
          </p>
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4" data-id="element-487" data-mp="2bm258i9lmxuv">
            <p className="text-sm text-emerald-800 font-medium" data-id="element-488" data-mp="2fm2hpxd50yza">
              Koppel je kind(eren) met hun 6-cijferige koppelcode.
            </p>
          </div>
          <p className="text-sm text-slate-400 font-medium animate-pulse" data-id="element-489" data-mp="1nv658xuzowcj">
            Naar het ouderportaal...
          </p>
        </motion.div>}
    </motion.div>;
}